import { z } from 'zod';
import { ClassIdSchema } from './classes.js';
/**
 * Spells (ADR 0008, ADR 0048): a class's grimoire, learnt along the regions,
 * four of them on the bar at a time. Data, not code (CLAUDE.md rule 4): the
 * effects are a small closed vocabulary the server resolves in
 * `rules/spells.ts` and `rules/statuses.ts`.
 *
 * Only the Warrior's are written for now — the one playable class.
 */
export const TARGETINGS = ['target', 'area', 'self', 'line'];
/** How many spells the bar holds: the keys 1 to 4 (ADR 0048). */
export const SPELL_BAR_SIZE = 4;
/**
 * How a spell looks when it goes off, drawn by the client from code
 * (`apps/client/src/game/spell-effects.ts`). A name, not a behaviour: two
 * spells may share a look, and changing it never changes what the spell does.
 * Each has its sound, `sfx.spell-<fx>`.
 */
export const SPELL_FX = [
    'strike',
    'whirlwind',
    'war-cry',
    'charge',
    'rend',
    'shield-wall',
    'shield-bash',
    'taunt',
    'seismic',
    'rage',
    'execute',
    'titan',
];
/**
 * Timed states a spell leaves (ADR 0048). Who carries each is fixed: the
 * first four fall on the creatures struck, the last two on the caster.
 */
export const MOB_STATUSES = ['stunned', 'slowed', 'bleeding', 'taunted'];
export const PLAYER_STATUSES = ['guarded', 'enraged'];
export const STATUSES = [...MOB_STATUSES, ...PLAYER_STATUSES];
export function isPlayerStatus(status) {
    return PLAYER_STATUSES.includes(status);
}
export const SpellEffectSchema = z.discriminatedUnion('kind', [
    /**
     * Damage as a multiple of the caster's basic hit; `execute` multiplies it
     * instead when the target is at or under that share of its health.
     */
    z.object({
        kind: z.literal('damage'),
        multiplier: z.number().positive(),
        execute: z
            .object({ below: z.number().positive().max(1), multiplier: z.number().positive() })
            .optional(),
    }),
    /** Heal a share of the caster's maximum health. */
    z.object({ kind: z.literal('heal'), maxHealthShare: z.number().positive().max(1) }),
    /** The caster pays a share of their maximum health; it never kills. */
    z.object({ kind: z.literal('sacrifice'), maxHealthShare: z.number().positive().max(1) }),
    /** The caster bounds to a free tile next to the target, before anything else lands. */
    z.object({ kind: z.literal('leap') }),
    /**
     * A timed state, on the creatures struck or on the caster, whichever
     * carries it. `power` reads by status: `slowed` multiplies the pace
     * (2 = half speed), `bleeding` is a share of the basic hit each second,
     * `guarded` the share of every blow taken away, `enraged` the share of dps
     * added. `stunned` and `taunted` ignore it.
     */
    z.object({
        kind: z.literal('status'),
        status: z.enum(STATUSES),
        durationMs: z.number().int().positive(),
        power: z.number().positive().default(1),
    }),
]);
/**
 * How a spell is learnt: from `level`, and once `quest` is turned in when it
 * names one — a region's signature spell is taught by its chain (ADR 0047).
 * Nothing is recorded: the grimoire is read from the level and the quests
 * done, so a character that already passed there knows it.
 */
export const SpellLearningSchema = z.object({
    level: z.number().int().min(1),
    quest: z.string().min(1).optional(),
});
export const SpellDefinitionSchema = z.object({
    id: z.string().min(1),
    classId: ClassIdSchema,
    /** French, shown in the tooltip of the spell bar and in the grimoire. */
    name: z.string().min(1),
    description: z.string().min(1),
    learn: SpellLearningSchema,
    /** Resource spent on cast. */
    cost: z.number().int().min(0),
    cooldownMs: z.number().int().min(0),
    targeting: z.enum(TARGETINGS),
    /**
     * Tiles. For `target`, how far the target may be; for `area`, the radius
     * around the caster; for `line`, its length ahead of the caster; ignored
     * for `self`.
     */
    range: z.number().int().min(0),
    /** Resolved in order: a leap moves the caster before the blow lands. */
    effects: z.array(SpellEffectSchema).min(1),
    /** Icon key in the manifest (`data/assets/src/ui/<icon>.png`). */
    icon: z.string().min(1),
    fx: z.enum(SPELL_FX),
});
/*
 * The Warrior's grimoire, in the order it is learnt: three in the meadow,
 * then two or three a region, the signature one taught by the region's chain.
 * Costs of 15 to 60 against a 100 bar refilling at 6 a second keep spells a
 * punctuation of the auto-attack (ADR 0008).
 */
const DEFINITIONS = [
    {
        id: 'power-strike',
        classId: 'warrior',
        name: 'Frappe puissante',
        description: 'Un coup appuyé sur ta cible : deux fois tes dégâts.',
        learn: { level: 1 },
        cost: 25,
        cooldownMs: 4000,
        targeting: 'target',
        range: 1,
        effects: [{ kind: 'damage', multiplier: 2 }],
        icon: 'spell-power-strike',
        fx: 'strike',
    },
    {
        id: 'war-cry',
        classId: 'warrior',
        name: 'Cri de guerre',
        description: 'Tu reprends ton souffle : un quart de ta vie revient.',
        learn: { level: 2 },
        cost: 30,
        cooldownMs: 20000,
        targeting: 'self',
        range: 0,
        effects: [{ kind: 'heal', maxHealthShare: 0.25 }],
        icon: 'spell-war-cry',
        fx: 'war-cry',
    },
    {
        id: 'whirlwind',
        classId: 'warrior',
        name: 'Tourbillon',
        description: 'Tu tournoies : tes dégâts à chaque ennemi autour de toi.',
        learn: { level: 3 },
        cost: 40,
        cooldownMs: 8000,
        targeting: 'area',
        range: 1,
        effects: [{ kind: 'damage', multiplier: 1.2 }],
        icon: 'spell-whirlwind',
        fx: 'whirlwind',
    },
    {
        id: 'charge',
        classId: 'warrior',
        name: 'Charge',
        description: "Tu bondis sur ta cible jusqu'à 6 cases, la frappes lourdement et l'étourdis trois secondes.",
        learn: { level: 5, quest: 'forest-wolves' },
        cost: 25,
        cooldownMs: 12000,
        targeting: 'target',
        range: 6,
        effects: [
            { kind: 'leap' },
            { kind: 'damage', multiplier: 1.8 },
            { kind: 'status', status: 'stunned', durationMs: 3000 },
        ],
        icon: 'spell-charge',
        fx: 'charge',
    },
    {
        id: 'rend',
        classId: 'warrior',
        name: 'Entaille',
        description: 'Une plaie ouverte : ta cible saigne pendant 6 secondes.',
        learn: { level: 7 },
        cost: 20,
        cooldownMs: 6000,
        targeting: 'target',
        range: 1,
        effects: [
            { kind: 'damage', multiplier: 0.5 },
            { kind: 'status', status: 'bleeding', durationMs: 6000, power: 0.35 },
        ],
        icon: 'spell-rend',
        fx: 'rend',
    },
    {
        id: 'shield-wall',
        classId: 'warrior',
        name: 'Mur de bouclier',
        description: 'Tu te couvres : 40 % de dégâts en moins pendant 5 secondes.',
        learn: { level: 8 },
        cost: 30,
        cooldownMs: 18000,
        targeting: 'self',
        range: 0,
        effects: [{ kind: 'status', status: 'guarded', durationMs: 5000, power: 0.4 }],
        icon: 'spell-shield-wall',
        fx: 'shield-wall',
    },
    {
        id: 'shield-bash',
        classId: 'warrior',
        name: 'Coup de bouclier',
        description: "Tu assommes ta cible deux secondes. Un boss ne s'étourdit pas, mais son attaque annoncée est retardée.",
        learn: { level: 10, quest: 'swamp-shaman' },
        cost: 25,
        cooldownMs: 12000,
        targeting: 'target',
        range: 1,
        effects: [
            { kind: 'damage', multiplier: 0.8 },
            { kind: 'status', status: 'stunned', durationMs: 2000 },
        ],
        icon: 'spell-shield-bash',
        fx: 'shield-bash',
    },
    {
        id: 'taunt',
        classId: 'warrior',
        name: 'Provocation',
        description: "Les ennemis à 3 cases s'en prennent à toi pendant 4 secondes. Un boss n'y cède pas.",
        learn: { level: 12, quest: 'desert-hyenas' },
        cost: 15,
        cooldownMs: 10000,
        targeting: 'area',
        range: 3,
        effects: [{ kind: 'status', status: 'taunted', durationMs: 4000 }],
        icon: 'spell-taunt',
        fx: 'taunt',
    },
    {
        id: 'seismic-wave',
        classId: 'warrior',
        name: 'Onde sismique',
        description: 'Tu frappes le sol : une onde sur 7 cases droit sur ta cible (devant toi sans cible), qui frappe et ralentit tous les ennemis sur son passage.',
        learn: { level: 14 },
        cost: 40,
        cooldownMs: 10000,
        targeting: 'line',
        range: 7,
        effects: [
            { kind: 'damage', multiplier: 1.5 },
            { kind: 'status', status: 'slowed', durationMs: 3000, power: 2 },
        ],
        icon: 'spell-seismic-wave',
        fx: 'seismic',
    },
    {
        id: 'rage',
        classId: 'warrior',
        name: 'Rage',
        description: 'Tu paies un dixième de ta vie : 40 % de DPS en plus pendant 8 secondes.',
        learn: { level: 16, quest: 'snow-saga' },
        cost: 20,
        cooldownMs: 30000,
        targeting: 'self',
        range: 0,
        effects: [
            { kind: 'sacrifice', maxHealthShare: 0.1 },
            { kind: 'status', status: 'enraged', durationMs: 8000, power: 0.4 },
        ],
        icon: 'spell-rage',
        fx: 'rage',
    },
    {
        id: 'execute',
        classId: 'warrior',
        name: 'Exécution',
        description: 'Tes dégâts, mais quatre fois tes dégâts si ta cible a moins d’un quart de sa vie.',
        learn: { level: 18 },
        cost: 35,
        cooldownMs: 8000,
        targeting: 'target',
        range: 1,
        effects: [{ kind: 'damage', multiplier: 1, execute: { below: 0.25, multiplier: 4 } }],
        icon: 'spell-execute',
        fx: 'execute',
    },
    {
        id: 'titan-wrath',
        classId: 'warrior',
        name: 'Colère du titan',
        description: 'Tu frappes la terre de toutes tes forces : trois fois tes dégâts à 3 cases autour de toi, qui étourdissent.',
        learn: { level: 20 },
        cost: 60,
        cooldownMs: 45000,
        targeting: 'area',
        range: 3,
        effects: [
            { kind: 'damage', multiplier: 3 },
            { kind: 'status', status: 'stunned', durationMs: 1500 },
        ],
        icon: 'spell-titan-wrath',
        fx: 'titan',
    },
];
export const SPELLS = Object.fromEntries(DEFINITIONS.map((raw) => {
    const spell = SpellDefinitionSchema.parse(raw);
    return [spell.id, spell];
}));
export function getSpell(id) {
    return SPELLS[id];
}
/** A class's whole grimoire, in the order it is learnt. */
export function spellsOf(classId) {
    return Object.values(SPELLS).filter((spell) => spell.classId === classId);
}
/** The spell a quest teaches, if it teaches one: its turn-in says so. */
export function spellTaughtBy(questId) {
    return Object.values(SPELLS).find((spell) => spell.learn.quest === questId);
}
//# sourceMappingURL=spells.js.map